import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BannerAlertBox, useToast } from '@entur/alert'
import { Badge } from '@entur/layout'
import { Loader } from '@entur/loader'
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@entur/tab'
import { space } from '@entur/tokens'
import { useApi, useApiOnLoad } from '../api-clients/client-hooks'
import { getEmployee, getSensitiveEmployeeFields } from '../api-clients/eba-clients'
import { FetchState } from '../api-clients/api-types'
import EmployeeTab from '../components/employee/employee-tab'
import FamilyMemberTab from '../components/family-member/family-member-tab'
import { DisabledContext, DisablingProvider } from '../contexts/disable-context'
import { EmployeeProvider, useEmployee } from '../contexts/employee-context'
import { trackEvent } from '../utils/tracking-utils'
import { BackButton } from '../components/layout/back-button'
import { FamilyMemberProvider, useFamilyMembers } from '../contexts/family-members-context'
import { findTicketRightsToSync } from '../utils/ticket-rights-utils'
import { EmployeeHeader } from '../components/employee/employee-header'
import FipTab from '../components/fip/fip-tab'
import { HistoryState } from '../types'
import { useClientInfo } from '../contexts/client-info-context'
import { ReadOnlyProvider, ReadOnlyContext } from '../contexts/read-only-context'
import { TravelTab } from '../components/travel/travel-tab'

export enum TabIndex {
  EMPLOYEE,
  FAMILY_MEMBERS,
  FIP,
  TRAVEL,
}

type TabsProps = {
  tabIndex: TabIndex
}
const EmployeeTabPanels: React.FC<TabsProps> = ({ tabIndex }) => {
  const { employee } = useEmployee()
  const { familyMembersResponse } = useFamilyMembers()

  const numberOfEmployeeTicketRightsToSync = findTicketRightsToSync(employee.ticketRights).length
  const numberOfFamilyMemberTicketRightsToSync = findTicketRightsToSync(
    familyMembersResponse.status === FetchState.SUCCESS
      ? familyMembersResponse.data.flatMap((it) => findTicketRightsToSync(it.ticketRights))
      : []
  ).length

  return (
    <div style={{ position: 'relative' }}>
      {numberOfEmployeeTicketRightsToSync > 0 && (
        <Badge
          variant="danger"
          type="notification"
          style={{
            position: 'absolute',
            top: '-57px',
            left: '131px',
          }}
        >
          {numberOfEmployeeTicketRightsToSync}
        </Badge>
      )}
      {numberOfFamilyMemberTicketRightsToSync > 0 && (
        <Badge
          variant="danger"
          type="notification"
          style={{
            position: 'absolute',
            top: '-57px',
            left: '316px',
          }}
        >
          {numberOfFamilyMemberTicketRightsToSync}
        </Badge>
      )}
      <TabPanels style={{ paddingTop: space.large, paddingLeft: 0, paddingRight: 0 }}>
        <TabPanel>
          <EmployeeTab />
        </TabPanel>
        <TabPanel>
          <FamilyMemberTab />
        </TabPanel>
        <TabPanel>
          <FipTab />
        </TabPanel>
        <TabPanel>
          <TravelTab tabIndex={tabIndex} />
        </TabPanel>
      </TabPanels>
    </div>
  )
}

const EmployeePageContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const locState = location.state as HistoryState

  const { addToast } = useToast()
  const { disabled } = useContext(DisabledContext)
  const { enable, disable } = useContext(ReadOnlyContext)
  const { hasSensitiveAccess } = useClientInfo()
  const { id } = useParams<{ id: string }>()
  const [currentId, setCurrentId] = useState(id)

  const tabIndices: { [key: string]: TabIndex } = {
    '#familiemedlemmer': TabIndex.FAMILY_MEMBERS,
    '#fip': TabIndex.FIP,
    '#reiser': TabIndex.TRAVEL,
    default: TabIndex.EMPLOYEE,
  }
  const [currentTabIndex, setCurrentTabIndex] = useState(tabIndices[location.hash])

  const [response, fetch] = useApiOnLoad(getEmployee, { id })
  const [sensitiveInfoResponse, fetchSensitiveInfo] = useApi(getSensitiveEmployeeFields)
  useEffect(() => {
    // Update component when url change
    if (currentId !== id) {
      setCurrentId(id)
      fetch({ id })
      if (hasSensitiveAccess === true) {
        fetchSensitiveInfo({ id })
      }
    }

    if (hasSensitiveAccess === true && sensitiveInfoResponse.status === FetchState.NOT_STARTED) {
      fetchSensitiveInfo({ id })
    }

    if (location.hash === '#familiemedlemmer' && currentTabIndex === TabIndex.EMPLOYEE) {
      setCurrentTabIndex(TabIndex.FAMILY_MEMBERS)
    }
    if (location.hash === '' && currentTabIndex === TabIndex.FAMILY_MEMBERS) {
      setCurrentTabIndex(TabIndex.EMPLOYEE)
    }
    if (location.hash === '#fip' && currentTabIndex === TabIndex.FIP) {
      setCurrentTabIndex(TabIndex.FIP)
    }
    if (location.hash === '#reiser' && currentTabIndex === TabIndex.TRAVEL) {
      setCurrentTabIndex(TabIndex.TRAVEL)
    }
  }, [
    id,
    currentId,
    currentTabIndex,
    fetchSensitiveInfo,
    fetch,
    hasSensitiveAccess,
    sensitiveInfoResponse,
    location.hash,
    location,
  ])

  useEffect(() => {
    if (location.hash.includes('ny')) {
      addToast('Rettighetseier opprettet')
      navigate(`/ansatt/${id}`, { state: locState, replace: true })
    }
    if (location.hash.includes('endret')) {
      addToast('Rettighetseier oppdatert')
      navigate(`/ansatt/${id}`, { state: locState, replace: true })
    }
    if (location.hash.includes('gjenopprettet')) {
      addToast('Rettighetseier gjenopprettet')
      navigate(`/ansatt/${id}`, { state: locState, replace: true })
      fetch({ id })
    }
  }, [id, location.hash, locState])

  useEffect(() => {
    if (response.status === FetchState.SUCCESS && response.data.draftEmployee) {
      trackEvent('View-Draft', { category: 'Draft' })
    }

    if (response.status === FetchState.SUCCESS && response.data) {
      if (response.data.isDeleted) {
        enable()
      } else {
        disable()
      }
    }
  }, [response, enable, disable])

  const shouldNotWaitForSensitiveInfo = sensitiveInfoResponse.status === FetchState.SUCCESS || !hasSensitiveAccess

  return (
    <div className="profile-page">
      {response.status !== FetchState.SUCCESS && <BackButton disabled={disabled} />}
      {response.status === FetchState.LOADING && <Loader>Behandler</Loader>}
      {response.status === FetchState.SUCCESS && shouldNotWaitForSensitiveInfo && (
        <EmployeeProvider
          initEmployee={(() => {
            const employee = response.data
            if (hasSensitiveAccess && sensitiveInfoResponse.status === FetchState.SUCCESS) {
              employee.sensitiveInfo = sensitiveInfoResponse.data
            }
            return employee
          })()}
        >
          <FamilyMemberProvider>
            <EmployeeHeader disabled={disabled} />
            <Tabs
              style={{
                clear: 'both',
              }}
              index={currentTabIndex}
              onChange={(newIndex) => {
                switch (newIndex) {
                  case TabIndex.EMPLOYEE:
                    navigate(`/ansatt/${id}`, { state: locState, replace: true })
                    break
                  case TabIndex.FAMILY_MEMBERS:
                    navigate(`/ansatt/${id}#familiemedlemmer`, { state: locState, replace: true })
                    break
                  case TabIndex.FIP:
                    navigate(`/ansatt/${id}#fip`, { state: locState, replace: true })
                    break
                  case TabIndex.TRAVEL:
                    navigate(`/ansatt/${id}#reiser`, { state: locState, replace: true })
                    break
                }
                setCurrentTabIndex(newIndex)
              }}
            >
              <TabList>
                <Tab data-cy="employee-tab-button" disabled={disabled}>
                  Rettighetseier
                </Tab>
                <Tab data-cy="family-member-tab-button" disabled={disabled}>
                  Familiemedlemmer
                </Tab>
                <Tab data-cy="employee-fip-tab" disabled={disabled}>
                  Utenlandsreiser
                </Tab>
                <Tab data-cy="employee-travel-tab" disabled={disabled}>
                  Innlandsreiser
                </Tab>
              </TabList>
              <EmployeeTabPanels tabIndex={currentTabIndex} />
            </Tabs>
          </FamilyMemberProvider>
        </EmployeeProvider>
      )}
      {response.status === FetchState.ERROR && (
        <BannerAlertBox
          variant="error"
          title="Kan ikke hente rettighetshaver"
          closable={false}
          style={{ marginBottom: '1em' }}
        >
          Det har skjedd en teknisk feil, ta kontakt med Entur support
        </BannerAlertBox>
      )}
    </div>
  )
}

const EmployeePage: React.FC = () => {
  return (
    <ReadOnlyProvider>
      <DisablingProvider>
        <EmployeePageContent />
      </DisablingProvider>
    </ReadOnlyProvider>
  )
}

export default EmployeePage
